import { getDocument } from "@faire/web--source/common/globals/getDocument";
import { getWindow } from "@faire/web--source/common/globals/getWindow";
import { singletonGetter } from "@faire/web--source/common/singletons/getter";
import { createStoreHookNonReactive } from "@faire/web--source/ui/hooks/useStoreNonReactive";

/**
 * Simple store, for stacking items that cause `.noscroll` on the
 * body.
 *
 * This singleton is safe to use inside a React component directly,
 * (without a hook wrapper) because it does not own or reference
 * any reactive state. It will be deprecated as soon as a suitable
 * hook is implemented which exposes the same functionality.
 */
export class ScrollStore {
  static get = singletonGetter(ScrollStore);

  private noScrollCounter: number = 0;

  private lastKnownScrollY: number = 0;

  constructor() {
    /**
     * The noscroll class will only prevent human interactions. In cases which we disable scrolling
     * but allow code to scroll in the background, we don't want to scroll back to it's original position
     * when we call popNoScroll. Thus, we are overwriting window.scrollTo to track the new position.
     *
     * More details in (https://github.com/Faire/web/pull/2603)
     */
    const window = getWindow();
    if (window) {
      const originalScrollTo = window.scrollTo;

      window.scrollTo = (optionsOrX?: any, y?: number) => {
        if (y === undefined) {
          originalScrollTo(optionsOrX);
        } else {
          originalScrollTo(optionsOrX, y);
        }
        this.lastKnownScrollY = y ?? optionsOrX?.top;
      };
    }
  }

  pushNoScroll = () => {
    if (this.noScrollCounter === 0) {
      this.lastKnownScrollY = getWindow()?.scrollY ?? 0;
      getDocument()?.body?.classList?.add("noscroll");
    }

    this.noScrollCounter += 1;
  };

  popNoScroll = () => {
    this.noScrollCounter = Math.max(0, this.noScrollCounter - 1);

    if (this.noScrollCounter === 0) {
      getDocument()?.body?.classList?.remove("noscroll");
      getWindow()?.scrollTo?.(0, this.lastKnownScrollY);
      this.lastKnownScrollY = 0;
    }
  };
}

// TODO(laszlopandy, jsmankoo): replace this store with a nicer
// hook which automatically pushes/pops on mount/umount of modal.
// NOTE terence.dickson@faire.com Check `usePopupChatEventListeners.ts` in
// the brand portal as well, as it also uses this to lock scrolling when the
// user is hovering over the pop-up chat.
export const useScrollStore = createStoreHookNonReactive(ScrollStore);
